import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import announcements from '../data/announcements';

const MegaphoneIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0118.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" />
  </svg>
);

const PlayIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const ExternalIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5h5v5m0-5L10 14M5 5h4m-4 0v14h14v-4" />
  </svg>
);

const formatDate = (iso) =>
  new Date(iso + 'T12:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

const AnnouncementsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main id="main-content" className="pt-0">
        {/* Hero */}
        <section
          className="relative pt-36 pb-20 bg-[#00213a] overflow-hidden"
        >
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{ height: '4rem', background: 'linear-gradient(to bottom, rgba(0,33,58,0) 0%, rgba(255,255,255,1) 100%)' }}
            aria-hidden="true"
          />

          <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                <MegaphoneIcon />
                <span className="text-sm font-semibold text-white tracking-tight">
                  School News
                </span>
              </div>
            </div>
            <h1
              className="text-5xl lg:text-7xl font-semibold text-white mb-6 tracking-tight"
              style={{ letterSpacing: '-0.02em' }}
            >
              Announcements
            </h1>
            <p className="text-2xl text-white/90 font-normal max-w-3xl leading-relaxed mx-auto">
              The latest updates, celebrations, and stories from our PS 96
              Lions community.
            </p>
          </div>
        </section>

        {/* Feed */}
        <section className="max-w-5xl mx-auto px-6 lg:px-8 mt-12 mb-20">
          {announcements.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-12 text-center text-gray-600">
              No posts yet. Check back soon for updates.
            </div>
          ) : (
            <div className="space-y-8">
              {announcements.map((post) => (
                <article
                  key={post.id}
                  className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200"
                >
                  {post.image && (
                    <a href={post.pdf || post.image} target="_blank" rel="noopener noreferrer" className="block bg-gray-50 border-b border-gray-200">
                      <img src={post.image} alt={post.imageAlt || post.title} className="w-full h-auto" />
                    </a>
                  )}
                  {post.flyers && (
                    <div className="grid sm:grid-cols-2 gap-px bg-gray-200 border-b border-gray-200">
                      {post.flyers.map((f) => (
                        <figure key={f.src} className="bg-white">
                          <img src={f.src} alt={f.alt} className="w-full h-auto" />
                          <figcaption className="px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#00213a]">{f.label}</figcaption>
                        </figure>
                      ))}
                    </div>
                  )}
                  {post.photos && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 bg-gray-50 border-b border-gray-200">
                      {post.photos.map((photo) => (
                        <img key={photo.src} src={photo.src} alt={photo.alt} className="w-full aspect-[3/4] object-cover" />
                      ))}
                    </div>
                  )}
                  {post.youtubeId && (
                    <div className="relative pb-[56.25%] h-0 bg-black">
                      <iframe
                        className="absolute top-0 left-0 w-full h-full"
                        src={`https://www.youtube.com/embed/${post.youtubeId}`}
                        title={post.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  )}
                  <div className="p-8">
                    <div className="flex items-center gap-2 text-xs font-semibold text-[#00213a] uppercase tracking-widest mb-3">
                      {post.youtubeId ? <PlayIcon /> : <MegaphoneIcon />}
                      <span>{post.youtubeId ? 'Video Post' : 'Announcement'}</span>
                      <span className="text-gray-500" aria-hidden="true">·</span>
                      <time dateTime={post.date} className="text-gray-500">
                        {formatDate(post.date)}
                      </time>
                    </div>
                    <h2
                      className="text-3xl font-semibold text-gray-900 mb-3 tracking-tight"
                      style={{ letterSpacing: '-0.02em' }}
                    >
                      {post.title}
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-5">
                      {post.summary}
                    </p>
                    {post.details && (
                      <div className="grid sm:grid-cols-2 gap-6 mb-6">
                        {post.details.map((group) => (
                          <div key={group.heading}>
                            <h3 className="text-sm font-bold uppercase tracking-widest text-[#00213a] mb-2">{group.heading}</h3>
                            <ul className="list-disc pl-5 space-y-1 text-gray-700 text-sm leading-relaxed">
                              {group.items.map((item) => <li key={item}>{item}</li>)}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                    {post.pdf && (
                      <a
                        href={post.pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-[#00213a] font-semibold hover:underline"
                      >
                        {post.pdfLabel || 'View the flyer (PDF)'} <ExternalIcon /><span className="sr-only"> (opens in a new tab)</span>
                      </a>
                    )}
                    {post.photoLink && (
                      <a href={post.photoLink} className="inline-flex items-center gap-2 text-[#00213a] font-semibold hover:underline">
                        See more photos in our gallery
                      </a>
                    )}
                    {post.link && (
                      <a
                        href={post.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-[#00213a] font-semibold hover:underline"
                      >
                        Watch on YouTube <ExternalIcon />
                      </a>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* eChalk CTA */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2
              className="text-3xl font-semibold text-gray-900 mb-4 tracking-tight"
              style={{ letterSpacing: '-0.02em' }}
            >
              Get Real-Time Updates
            </h2>
            <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
              Download the eChalk Notify app and connect with code{' '}
              <span className="font-mono font-bold text-[#00213a]">9L38X2</span>{' '}
              to receive instant alerts from PS 96.
            </p>
            <a
              href="https://www.echalk.com/notify"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#00213a] text-white font-semibold rounded-lg hover:bg-[#003a5d] transition-colors"
            >
              Learn About eChalk Notify <ExternalIcon />
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AnnouncementsPage;
